import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, FileText, Plus, Send, X, History, ArrowLeft, Pencil, Trash2, Settings as SettingsIcon, Search, Download, ExternalLink, Calculator } from 'lucide-react';
import { MenuPlus } from './MenuPlus';
import { moduleApi } from '../../lib/api';
import { buildDocumentFilename } from '../../lib/documentFilename';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/Button';
import { generateDevisPrestationPdf, DevisPrestationLine } from './devisPrestationPdfGenerator';
import { DevisPrestationComposer } from './DevisPrestationComposer';
import { ReglagesDossiers } from './ReglagesDossiers';
import { useConfirm } from '../ui/ConfirmProvider';

interface CatalogItem { id: string; label: string; unit_price_ttc: number; tax_rate: number; unit_type?: string; }
interface FormulaSubscription { id: string; formula_id: string; formula_name: string; occurrences_used: number; occurrences_total: number; status: string; }
interface DevisRow {
    id: string; devis_number?: string | null; dossier_id: string; client_label: string; email: string; status: string;
    dossier_stage: string; total_ttc: number; created_at: string; public_token?: string | null;
    downloaded_at?: string | null;
}

const fmtMontant = (n?: number | null) =>
    typeof n === 'number' ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n) : '—';
const fmtDate = (d?: string | null) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; }
};

const STATUS_LABELS: Record<string, string> = { brouillon: 'Brouillon', envoye: 'Envoyé', signe: 'Signé', refuse: 'Refusé', abandonne: 'Abandonné' };
const STATUS_STYLES: Record<string, string> = {
    brouillon: 'bg-surface-soft text-content-muted',
    envoye: 'bg-amber-500/10 text-amber-500',
    signe: 'bg-green-500/10 text-green-500',
    refuse: 'bg-red-500/10 text-red-400',
};

const HISTORY_ACTION_LABELS: Record<string, string> = {
    cree: 'Devis créé',
    envoye: 'Devis envoyé',
    renvoye: 'Devis renvoyé (même mail)',
    relance: 'Relance envoyée',
    signe_recu: 'Signature reçue du client',
    signe_valide: 'Signature validée par l’établissement',
    refuse: 'Devis refusé par le client',
};

const fmtDateHeure = (d?: string | null) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return '—'; }
};

// Petite modale « Historique » d'un devis — même table simple que le reste du module, pas besoin de plus.
function HistoriqueDevisModal({ devisId, onClose }: { devisId: string; onClose: () => void }) {
    const [loading, setLoading] = React.useState(true);
    const [rows, setRows] = React.useState<any[]>([]);
    const [sentTo, setSentTo] = React.useState<string | null>(null);
    const [sentAt, setSentAt] = React.useState<string | null>(null);
    const [sentSubject, setSentSubject] = React.useState<string | null>(null);
    const [sentHtml, setSentHtml] = React.useState<string | null>(null);
    const [apercuOuvert, setApercuOuvert] = React.useState(false);
    const [erreur, setErreur] = React.useState('');

    React.useEffect(() => {
        moduleApi.getPrestationDevisHistory(devisId)
            .then((r: any) => {
                if (r?.error) { setErreur(r.error); return; }
                setRows(Array.isArray(r?.rows) ? r.rows : []);
                setSentTo(r?.sent_to || null); setSentAt(r?.sent_at || null);
                setSentSubject(r?.sent_subject || null); setSentHtml(r?.sent_html || null);
            })
            .catch((e: any) => setErreur(e?.message || 'Erreur de chargement'))
            .finally(() => setLoading(false));
    }, [devisId]);

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-surface border border-border rounded-2xl p-5 w-full max-w-md space-y-4 max-h-[80vh] overflow-y-auto">
                <div className="flex items-center justify-between">
                    <p className="text-base font-black text-content">Historique du devis</p>
                    <button onClick={onClose} className="text-content-muted hover:text-content"><X size={18} /></button>
                </div>
                {sentTo && (
                    <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-1.5">
                        <p className="text-xs text-content-muted">Envoyé à <span className="font-bold text-content">{sentTo}</span>{sentAt && <span> — le <span className="font-bold text-content">{fmtDateHeure(sentAt)}</span></span>}</p>
                        {sentHtml && (
                            <button onClick={() => setApercuOuvert((v) => !v)} className="text-xs font-bold text-brand-600 dark:text-brand-400 hover:underline">
                                {apercuOuvert ? "Masquer l'aperçu du mail" : 'Voir l\'aperçu du mail reçu'}
                            </button>
                        )}
                        {apercuOuvert && sentHtml && (
                            <div className="rounded-lg border border-border overflow-hidden bg-white" style={{ height: '50vh' }}>
                                {sentSubject && <p className="text-[11px] font-bold text-slate-700 bg-slate-50 px-2 py-1.5 border-b border-slate-200">{sentSubject}</p>}
                                <iframe srcDoc={sentHtml} title="Aperçu du mail" className="w-full h-full border-0" />
                            </div>
                        )}
                    </div>
                )}
                {erreur && <p className="text-sm text-red-500 font-semibold">{erreur}</p>}
                {loading ? (
                    <div className="flex justify-center py-8"><Loader2 size={22} className="animate-spin text-content-muted" /></div>
                ) : rows.length === 0 ? (
                    <p className="text-sm text-content-muted py-4 text-center">Aucun événement pour ce devis.</p>
                ) : (
                    <ul className="divide-y divide-border">
                        {rows.map((r) => (
                            <li key={r.id} className="py-2.5">
                                <p className="text-sm font-bold text-content">{HISTORY_ACTION_LABELS[r.action] || r.action}</p>
                                {r.note && <p className="text-xs text-content-muted mt-0.5">{r.note}</p>}
                                <p className="text-[11px] text-content-muted mt-0.5">{fmtDateHeure(r.created_at)}{r.actor_label ? ` — ${r.actor_label}` : ''}</p>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

/**
 * Module « Devis » autonome — même esprit que Factures : un devis pour n'importe quel contact CRM, avec
 * choix libre des prestations du catalogue et, en option, une formule active du client. Sous le capot un
 * devis reste rattaché à un prestation_dossiers (le serveur en crée un discrètement si aucun n'est choisi)
 * — voir resolveOrCreateDossierForDevisModule côté API pour la raison de ce choix.
 */
export function DevisPrestationModule() {
    const { user } = useAuth();
    const confirmAction = useConfirm();
    // Un devis déjà téléchargé ou envoyé ne peut plus être supprimé, sauf par le super-admin — même règle
    // que la facture (InvoiceHistory.tsx).
    const isSuperAdminSession = Boolean(
        (user as any)?.type === 'admin' ||
        ((user as any)?.type === 'client' && (user as any)?.impersonatedBySuperAdmin)
    );
    const companyName = (user as any)?.company_name || (user as any)?.companyName || (user as any)?.name || '';
    const logoUrl = (user as any)?.logoUrl || (user as any)?.logo_url || null;
    // Coordonnées complètes de l'établissement pour le PDF (adresse, SIRET, TVA...), sur le même modèle que
    // Factures/CreateInvoice.tsx — le devis se limitait avant au seul nom de l'établissement.
    const [etablissement, setEtablissement] = React.useState<any>(null);
    React.useEffect(() => { moduleApi.getBillingSettings().then((r: any) => setEtablissement(r || null)).catch(() => {}); }, []);
    // Reconstruit les coordonnées client (adresse) pour le PDF à partir du contact CRM du dossier — le
    // dossier lui-même ne stocke que nom/email/téléphone, pas l'adresse.
    const clientInfoDuDetail = async (detail: any) => {
        const d = detail.dossier || {};
        const label = d.type === 'PROFESSIONNEL' ? (d.company_name || 'Sans nom') : `${d.first_name || ''} ${d.last_name || ''}`.trim() || 'Sans nom';
        let client: any = { email: d.email, phone: d.phone };
        if (d.crm_contact_id) {
            try { const c: any = await moduleApi.getCRMContact(d.crm_contact_id); if (c && !c.error) client = { address: c.address, postal_code: c.postal_code, city: c.city, email: c.email || d.email, phone: c.phone || d.phone }; } catch {}
        }
        return { label, client };
    };
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [filtre, setFiltre] = React.useState<'tous' | 'brouillon' | 'envoye' | 'signe' | 'refuse' | 'abandonne'>('tous');
    const [recherche, setRecherche] = React.useState('');
    const [rows, setRows] = React.useState<DevisRow[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [erreur, setErreur] = React.useState('');
    const [envoiEnCoursId, setEnvoiEnCoursId] = React.useState<string | null>(null);
    const [historiqueDevisId, setHistoriqueDevisId] = React.useState<string | null>(null);
    const [editionDevisId, setEditionDevisId] = React.useState<string | null>(null);
    const [parametresOuverts, setParametresOuverts] = React.useState(false);
    const [suppressionEnCoursId, setSuppressionEnCoursId] = React.useState<string | null>(null);

    const supprimerDevis = async (devisId: string) => {
        if (suppressionEnCoursId) return;
        if (!(await confirmAction({ title: 'Supprimer le devis ?', description: <>Le devis sera effacé définitivement.</>, confirmLabel: 'Supprimer le devis' }))) return;
        setSuppressionEnCoursId(devisId);
        setErreur('');
        try {
            const res: any = await moduleApi.deletePrestationDevis(devisId);
            if (res?.error) { setErreur(res.error); return; }
            charger();
        } catch (e: any) {
            setErreur(e?.message || 'Erreur lors de la suppression du devis.');
        } finally { setSuppressionEnCoursId(null); }
    };

    // Régénère le PDF à partir des lignes déjà stockées sur le devis (pas besoin de retenir le PDF généré
    // à la composition) puis appelle /send — même logique de brouillon->envoi que le composeur de la modale.
    const envoyerDevis = async (devisId: string) => {
        if (envoiEnCoursId) return;
        setEnvoiEnCoursId(devisId);
        setErreur('');
        try {
            const detail: any = await moduleApi.getPrestationDevisDetail(devisId);
            if (detail?.error) { setErreur(detail.error); return; }
            const { label, client } = await clientInfoDuDetail(detail);
            const { base64 } = await generateDevisPrestationPdf({
                companyName, logoUrl, etablissement, clientLabel: label, client, devisNumber: detail.devis_number || null,
                lines: detail.items as DevisPrestationLine[], totalHt: detail.total_ht, totalTva: detail.total_tva, totalTtc: detail.total_ttc,
            });
            const envoi: any = await moduleApi.sendPrestationDevisStandalone(devisId, { document_base64: base64, document_file_name: buildDocumentFilename(companyName, 'DEVIS', detail.devis_number) });
            if (envoi?.error) { setErreur(envoi.error); return; }
            charger();
        } catch (e: any) {
            setErreur(e?.message || "Erreur lors de l'envoi du devis.");
        } finally { setEnvoiEnCoursId(null); }
    };

    const [telechargementEnCoursId, setTelechargementEnCoursId] = React.useState<string | null>(null);
    // Régénère le PDF (mêmes lignes déjà stockées) et le télécharge — ne renvoie rien au client, ne change
    // rien en base, utilisable pour un devis en brouillon comme déjà envoyé.
    const telechargerDevis = async (devisId: string) => {
        if (telechargementEnCoursId) return;
        setTelechargementEnCoursId(devisId);
        setErreur('');
        try {
            const detail: any = await moduleApi.getPrestationDevisDetail(devisId);
            if (detail?.error) { setErreur(detail.error); return; }
            const { label, client } = await clientInfoDuDetail(detail);
            const { blob } = await generateDevisPrestationPdf({
                companyName, logoUrl, etablissement, clientLabel: label, client, devisNumber: detail.devis_number || null,
                lines: detail.items as DevisPrestationLine[], totalHt: detail.total_ht, totalTva: detail.total_tva, totalTtc: detail.total_ttc,
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = buildDocumentFilename(companyName, 'DEVIS', detail.devis_number); a.click();
            URL.revokeObjectURL(url);
            // Marque le devis comme téléchargé — au-delà, seul le super-admin pourra le supprimer.
            try { await moduleApi.markPrestationDevisDownloaded(devisId); } catch { /* non bloquant */ }
            charger();
        } catch (e: any) {
            setErreur(e?.message || 'Erreur lors du téléchargement.');
        } finally { setTelechargementEnCoursId(null); }
    };
    // Ouvert directement (et pré-rempli) quand on arrive depuis « Créer un devis » sur un dossier de
    // Suivi de prestation — voir DossierPrestationPipelinePage.tsx.
    const contactPreselectionne = searchParams.get('contact') || '';
    const dossierPreselectionne = searchParams.get('dossier') || '';
    const [creation, setCreation] = React.useState(!!contactPreselectionne || !!dossierPreselectionne);

    const charger = React.useCallback(() => {
        setLoading(true);
        moduleApi.getPrestationDevisList()
            .then((r: any) => setRows(Array.isArray(r) ? r : []))
            .catch((e: any) => setErreur(e?.message || 'Erreur de chargement'))
            .finally(() => setLoading(false));
    }, []);

    React.useEffect(() => { charger(); }, [charger]);

    if (creation || editionDevisId) {
        return (
            <div className="p-6 max-w-7xl mx-auto space-y-5">
                <button
                    onClick={() => { setCreation(false); setEditionDevisId(null); setSearchParams({}); }}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-content-muted hover:text-content"
                >
                    <ArrowLeft size={14} /> Retour à la liste des devis
                </button>
                <DevisPrestationComposer
                    companyName={companyName}
                    logoUrl={logoUrl}
                    dossierId={dossierPreselectionne || null}
                    initialContactId={contactPreselectionne || null}
                    editingDevisId={editionDevisId}
                    onSent={() => { setCreation(false); setEditionDevisId(null); setSearchParams({}); charger(); }}
                    onHistory={(id) => setHistoriqueDevisId(id)}
                    onReset={() => { setCreation(false); setEditionDevisId(null); setSearchParams({}); charger(); }}
                />
                {historiqueDevisId && (
                    <HistoriqueDevisModal devisId={historiqueDevisId} onClose={() => setHistoriqueDevisId(null)} />
                )}
            </div>
        );
    }

    // Un devis envoyé sans réponse depuis 5 jours ou plus mérite une relance.
    const JOUR = 86400000;
    const aRelancer = (r: DevisRow) => r.status === 'envoye' && Date.now() - new Date((r as any).sent_at || r.created_at).getTime() >= 5 * JOUR;
    const compte = (statut: string) => rows.filter((r) => r.status === statut).length;
    const FILTRES: { cle: typeof filtre; label: string; n: number }[] = [
        { cle: 'tous', label: 'Tous', n: rows.length },
        { cle: 'brouillon', label: 'Brouillons', n: compte('brouillon') },
        { cle: 'envoye', label: 'En attente de réponse', n: compte('envoye') },
        { cle: 'signe', label: 'Signés', n: compte('signe') },
        { cle: 'refuse', label: 'Refusés', n: compte('refuse') },
        ...(compte('abandonne') > 0 ? [{ cle: 'abandonne' as typeof filtre, label: 'Abandonnés', n: compte('abandonne') }] : []),
    ];
    const q = recherche.trim().toLowerCase();
    const visibles = rows
        .filter((r) => filtre === 'tous' || r.status === filtre)
        .filter((r) => !q || [r.client_label, r.email, r.devis_number].some((v) => String(v || '').toLowerCase().includes(q)));
    const nbARelancer = rows.filter(aRelancer).length;
    const jourFr = (d?: string | null) => {
        if (!d) return '—';
        const jours = Math.floor((Date.now() - new Date(d).getTime()) / JOUR);
        if (jours <= 0) return "aujourd'hui";
        if (jours === 1) return 'hier';
        if (jours < 30) return 'il y a ' + jours + ' jours';
        return fmtDate(d);
    };

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-xl font-black text-content flex items-center gap-2.5">
                        <FileText size={22} className="text-brand-600 dark:text-brand-400" /> Devis
                    </h1>
                    <p className="text-sm text-content-muted mt-1">Les devis envoyés à vos clients. Pour chiffrer sans engagement, utilisez l'estimation rapide.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => setParametresOuverts(true)} title="Régler les devis : façon de travailler, délais, e-mails, signature"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-surface border border-border text-content-muted hover:text-content hover:border-brand-500 transition-colors">
                        <SettingsIcon size={14} /> Réglages
                    </button>
                    <button onClick={() => navigate('/estimation-devis')}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-surface border border-border text-content hover:border-brand-500 transition-colors">
                        <Calculator size={14} /> Estimation rapide
                    </button>
                    <Button onClick={() => setCreation(true)}><Plus size={15} /> Nouveau devis</Button>
                </div>
            </div>

            {erreur && <p className="text-sm text-red-500 font-semibold">{erreur}</p>}

            {!loading && rows.length > 0 && (
                <div className="space-y-3">
                    {nbARelancer > 0 && (
                        <button type="button" onClick={() => setFiltre('envoye')}
                            className="w-full text-left rounded-xl border border-amber-500/30 bg-amber-500/10 px-3.5 py-2.5 text-sm text-amber-700 dark:text-amber-400 font-semibold hover:bg-amber-500/15">
                            {nbARelancer} devis envoyé{nbARelancer > 1 ? 's' : ''} sans réponse depuis plus de 5 jours : pensez à relancer.
                        </button>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                        {FILTRES.map((f) => (
                            <button key={f.cle} type="button" onClick={() => setFiltre(f.cle)}
                                className={'px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ' + (filtre === f.cle ? 'border-brand-500 bg-brand-500/10 text-brand-600 dark:text-brand-400' : 'border-border text-content-muted hover:text-content')}>
                                {f.label} <span className="opacity-60">{f.n}</span>
                            </button>
                        ))}
                        <div className="relative ml-auto w-full sm:w-64">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
                            <input type="search" value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="Client, e-mail ou numéro…"
                                className="w-full bg-surface border border-border rounded-lg pl-8 pr-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 size={26} className="animate-spin text-content-muted" /></div>
            ) : rows.length === 0 ? (
                <div className="text-center py-16 text-content-muted">
                    <FileText size={28} className="mx-auto mb-2 opacity-40" />
                    <p className="text-sm">Aucun devis pour le moment.</p>
                    <Button className="mt-4" onClick={() => setCreation(true)}><Plus size={15} /> Créer le premier devis</Button>
                </div>
            ) : visibles.length === 0 ? (
                <p className="text-center py-12 text-sm text-content-muted">Aucun devis ne correspond à ce filtre.</p>
            ) : (
                <ul className="space-y-2.5">
                    {visibles.map((r) => {
                        const relancer = aRelancer(r);
                        const peutSupprimer = (r.status === 'brouillon' && !r.downloaded_at) || isSuperAdminSession;
                        // Une seule action principale selon l'état du devis ; le reste vit dans « Plus ».
                        const principale = r.status === 'brouillon'
                            ? { texte: envoiEnCoursId === r.id ? 'Envoi…' : 'Envoyer', action: () => envoyerDevis(r.id), icone: <Send size={13} /> }
                            : r.status === 'envoye'
                                ? { texte: envoiEnCoursId === r.id ? 'Envoi…' : 'Relancer', action: () => envoyerDevis(r.id), icone: <Send size={13} /> }
                                : { texte: telechargementEnCoursId === r.id ? 'Préparation…' : 'Télécharger', action: () => telechargerDevis(r.id), icone: <Download size={13} /> };
                        return (
                            <li key={r.id} className="rounded-2xl border border-border bg-surface p-4 flex items-center gap-4 flex-wrap hover:border-brand-500/40 transition-colors">
                                <div className="min-w-0 flex-1 basis-56">
                                    <p className="font-bold text-content truncate">{r.client_label}</p>
                                    <p className="text-xs text-content-muted truncate">{r.email || 'Sans e-mail'}</p>
                                    <p className="text-[11px] text-content-muted mt-0.5">{r.devis_number || 'Sans numéro'} · créé {jourFr(r.created_at)}</p>
                                </div>
                                <div className="text-right shrink-0">
                                    <p className="text-lg font-black text-content">{fmtMontant(r.total_ttc)}</p>
                                    <span className={'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold ' + (relancer ? 'bg-amber-500/10 text-amber-600' : (STATUS_STYLES[r.status] || STATUS_STYLES.brouillon))}>
                                        {relancer ? 'À relancer' : (STATUS_LABELS[r.status] || r.status)}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 shrink-0 ml-auto">
                                    <button onClick={principale.action} disabled={envoiEnCoursId === r.id || telechargementEnCoursId === r.id}
                                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold disabled:opacity-50 transition-colors">
                                        {principale.icone} {principale.texte}
                                    </button>
                                    <MenuPlus items={[
                                        ...(r.status === 'brouillon' ? [{ cle: 'edit', icone: <Pencil size={14} />, texte: 'Modifier', action: () => setEditionDevisId(r.id) }] : []),
                                        ...(r.status !== 'brouillon' ? [{ cle: 'renvoi', icone: <Send size={14} />, texte: 'Renvoyer le devis', disabled: envoiEnCoursId === r.id, action: () => envoyerDevis(r.id) }] : []),
                                        ...(r.status !== 'signe' ? [{ cle: 'dl', icone: <Download size={14} />, texte: 'Télécharger en PDF', disabled: telechargementEnCoursId === r.id, action: () => telechargerDevis(r.id) }] : []),
                                        ...(r.public_token ? [{ cle: 'lien', icone: <ExternalLink size={14} />, texte: 'Voir la page du client', action: () => window.open('/prestation-devis/' + r.public_token, '_blank') }] : []),
                                        { cle: 'hist', icone: <History size={14} />, texte: 'Historique des envois', action: () => setHistoriqueDevisId(r.id) },
                                        ...(peutSupprimer ? [{ cle: 'del', icone: <Trash2 size={14} />, texte: suppressionEnCoursId === r.id ? 'Suppression…' : 'Supprimer', danger: true, disabled: suppressionEnCoursId === r.id, action: () => supprimerDevis(r.id) }] : []),
                                    ]} />
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}

            {historiqueDevisId && (
                <HistoriqueDevisModal devisId={historiqueDevisId} onClose={() => setHistoriqueDevisId(null)} />
            )}
            {parametresOuverts && (
                <ReglagesDossiers onClose={() => setParametresOuverts(false)} />
            )}
        </div>
    );
}
